import './EditWorkTicketModal.scss';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/CloseSharp';
import SaveIcon from '@mui/icons-material/SaveSharp';
import CheckCircleIcon from '@mui/icons-material/CheckCircleSharp';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import Button from 'src/component/UI/Button';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import { fetchDeviceTags, clearDeviceTags } from 'src/module/device/action';
import { getDeviceTags, getLoadingDeviceTags } from 'src/module/device/selector';
import { getSingularWorkTicketLabel } from 'src/module/authentication/selector';
import { formatCircuitTag } from 'src/utils/utils';
import moment from 'moment';

type CircuitOption = {
  label: string;
  value: string;
  tag: string;
};

type SelectedCircuit = {
  id: string;
  tag: string;
  label: string;
};

export default function EditWorkTicketModal (props) {
  const {
    handleSubmit,
    handleClose,
    handleComplete,
    open,
    workTicket,
    existingCircuits,
    readonly
  } = props;

  const dispatch = useDispatch();
  const deviceTags = useSelector(getDeviceTags);
  const loadingDeviceTags = useSelector(getLoadingDeviceTags);
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);

  // Form fields
  const [notificationId, setNotificationId] = useState('');
  const [description, setDescription] = useState('');
  const [revision, setRevision] = useState('');
  const [note, setNote] = useState('');

  // Circuit management
  const [selectedCircuits, setSelectedCircuits] = useState<SelectedCircuit[]>([]);
  const [options, setOptions] = useState<CircuitOption[]>([]);
  const [inputValue, setInputValue] = useState('');

  // Track if changes were made
  const [hasChanges, setHasChanges] = useState(false);
  const [circuitsChanged, setCircuitsChanged] = useState(false);

  // Initialize form with work ticket data
  useEffect(() => {
    if (workTicket && open) {
      setNotificationId(workTicket.get('sap_notification_id') || '');
      setDescription(workTicket.get('description') || '');
      setRevision(workTicket.get('revision') || '');
      setNote(workTicket.get('note') || '');
      setHasChanges(false);
      setCircuitsChanged(false);
    }
  }, [workTicket, open]);

  // Initialize with existing circuits
  useEffect(() => {
    if (existingCircuits && existingCircuits.length > 0) {
      setSelectedCircuits(existingCircuits);
    } else {
      setSelectedCircuits([]);
    }
  }, [existingCircuits, open]);

  // Build options from fetched device tags
  useEffect(() => {
    if (!loadingDeviceTags) {
      const optionsArray: CircuitOption[] = [];

      deviceTags.forEach((option) => {
        const optionId = option.get('id');
        const label = option.get('type') === 'circuit'
          ? formatCircuitTag(option.get('tag'), option.get('controller_tag'))
          : option.get('tag');

        // Filter out already selected circuits
        const isAlreadySelected = selectedCircuits.some(c => c.id === optionId);
        if (!isAlreadySelected) {
          optionsArray.push({
            label,
            value: optionId,
            tag: option.get('tag')
          });
        }
      });

      setOptions(optionsArray);
    }
  }, [loadingDeviceTags, deviceTags, selectedCircuits]);

  // Handle field changes
  const handleFieldChange = (setter) => (e) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  // Fetch device tags when input changes
  const handleInputChange = (e, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue && newInputValue.length >= 3) {
      dispatch(clearDeviceTags());
      dispatch(fetchDeviceTags(newInputValue, 'circuit'));
    }
  };

  // Add circuit to selection
  const handleCircuitSelect = (e, selectedOption: CircuitOption | null) => {
    if (selectedOption && selectedOption.value) {
      const newCircuit: SelectedCircuit = {
        id: selectedOption.value,
        tag: selectedOption.tag,
        label: selectedOption.label
      };

      if (!selectedCircuits.some(c => c.id === newCircuit.id)) {
        setSelectedCircuits([...selectedCircuits, newCircuit]);
        setCircuitsChanged(true);
      }

      setInputValue('');
    }
  };

  // Remove circuit from selection
  const handleRemoveCircuit = (circuitId: string) => {
    setSelectedCircuits(selectedCircuits.filter(c => c.id !== circuitId));
    setCircuitsChanged(true);
  };

  // Submit the changes
  const onSubmit = () => {
    const updatedData = {
      description: description.trim(),
      revision: revision.trim(),
      note: note.trim()
    };

    handleSubmit(updatedData, circuitsChanged ? selectedCircuits : null);
    handleClose();
  };

  // Handle mark as complete
  const onComplete = () => {
    if (handleComplete) {
      handleComplete(workTicket.get('id'), workTicket.get('sap_notification_id'));
    }
    handleClose();
  };

  // Clear state when modal closes
  const onClose = () => {
    setInputValue('');
    handleClose();
  };

  const isCompleted = workTicket?.get('completed_at') != null;
  const createdAt = workTicket?.get('notification_created_at');
  const completedAt = workTicket?.get('completed_at');
  const canSave = hasChanges || circuitsChanged;

  return (
    <Modal
      data-testid='edit-work-ticket-modal'
      open={open}
      onClose={onClose}
      aria-labelledby='edit-work-ticket-modal-title'
    >
      <div className='modal edit-work-ticket-modal'>
        <div className='modal-header'>
          <h2 id='edit-work-ticket-modal-title'>
            {readonly ? 'View' : 'Edit'} {singularWorkTicketLabel}
          </h2>
          <IconButton className='close-btn' onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </div>

        <div className='modal-content'>
          {/* Status indicator */}
          {isCompleted && (
            <div className='status-badge completed'>
              <CheckCircleIcon fontSize='small' />
              <span>Completed {completedAt ? moment(completedAt).format('YYYY-MM-DD HH:mm') : ''}</span>
            </div>
          )}

          {/* Work ticket info header */}
          <div className='work-ticket-info'>
            <div className='info-item'>
              <span className='label'>{singularWorkTicketLabel} ID:</span>
              <span className='value' style={{ fontWeight: 600 }}>{notificationId || 'N/A'}</span>
            </div>
            {createdAt && (
              <div className='info-item'>
                <span className='label'>Created:</span>
                <span className='value'>{moment(createdAt).format('MMM D, YYYY h:mm A')}</span>
              </div>
            )}
          </div>

          <Divider sx={{ my: 2 }} />

          {/* Editable fields */}
          <div className='form-fields'>
            <TextField
              label='Description'
              value={description}
              onChange={handleFieldChange(setDescription)}
              fullWidth
              variant='outlined'
              placeholder='Enter description...'
              InputLabelProps={{ shrink: true }}
              disabled={readonly || isCompleted}
              multiline
              rows={2}
            />

            <div className='two-column'>
              <TextField
                label='Revision'
                value={revision}
                onChange={handleFieldChange(setRevision)}
                fullWidth
                variant='outlined'
                placeholder='Enter revision...'
                InputLabelProps={{ shrink: true }}
                disabled={readonly || isCompleted}
              />

              <TextField
                label='Note'
                value={note}
                onChange={handleFieldChange(setNote)}
                fullWidth
                variant='outlined'
                placeholder='Enter note...'
                InputLabelProps={{ shrink: true }}
                disabled={readonly || isCompleted}
              />
            </div>
          </div>

          <Divider sx={{ my: 2 }} />

          {/* Circuit management section */}
          <div className='circuits-section'>
            <h3>Circuits ({selectedCircuits.length})</h3>

            {!readonly && !isCompleted && (
              <div className='circuit-selector'>
                <Autocomplete
                  data-testid='circuit-autocomplete'
                  options={options}
                  loading={loadingDeviceTags}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  onChange={handleCircuitSelect}
                  value={null}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add circuits"
                      placeholder="Type to search circuits (min 3 chars)..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDeviceTags ? <LogoSpinner size={24} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </div>
            )}

            <div className='circuits-list'>
              {selectedCircuits.length === 0 ? (
                <div className='empty-state'>
                  No circuits assigned. {!readonly && !isCompleted && 'Use the search above to add circuits.'}
                </div>
              ) : (
                selectedCircuits.map((circuit) => (
                  <div key={circuit.id} className='circuit-chip'>
                    <span>{circuit.label}</span>
                    {!readonly && !isCompleted && selectedCircuits.length > 1 && (
                      <Tooltip title='Remove circuit'>
                        <IconButton
                          size='small'
                          onClick={() => handleRemoveCircuit(circuit.id)}
                          className='remove-btn'
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className='button-bar'>
          <Button onClick={onClose} className='cancel-btn'>Cancel</Button>
          {!readonly && !isCompleted && (
            <>
              {handleComplete && (
                <Button
                  onClick={onComplete}
                  icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                  className='complete-btn'
                >
                  Mark Complete
                </Button>
              )}
              <Button
                onClick={onSubmit}
                icon={<SaveIcon style={{ fontSize: 14 }} />}
                cta
                disabled={!canSave}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

EditWorkTicketModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleComplete: PropTypes.func,
  open: PropTypes.bool.isRequired,
  workTicket: PropTypes.object,
  existingCircuits: PropTypes.array,
  readonly: PropTypes.bool
};

EditWorkTicketModal.defaultProps = {
  workTicket: null,
  existingCircuits: [],
  readonly: false,
  handleComplete: null
};
